import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { FormSingleSelect } from '@components/forms/dropdowns/base/FormSingleSelect';
import { keymap } from '@utils/helper';
import { useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';

import type { SyntheticEvent } from 'react';
import type { FormComponentProps } from '../types';

const defaults = {
    reseller: { ResellerID: null, ResellerName: '' },
    location: { LocationID: null, LocationName: '' },
};

type DropProps<State extends Record<string, any>> = FormComponentProps<State> & {
    options: {
        companies: boolean;
        resellers: boolean;
        locations: boolean;
    };
    size?: number;
    disabled?: boolean;
    disabledOptions?: Record<string, boolean>;
    includeAll?: boolean;
    onLocationChange?: (_: SyntheticEvent<Element, Event>, value: any) => void;
};

export const OrgDropDowns = <State extends Record<string, any>>({
    draft,
    options,
    size,
    disabled = false,
    disabledOptions = {},
    includeAll = false,
    onLocationChange,
}: DropProps<State>) => {
    const [draftState, setDraftState] = draft;

    if (options.resellers && !options.companies) {
        throw new Error('Must set options.companies to "true" if options.resellers is set to "true"!');
    }

    const { IdToken } = useSelector((state: RootState) => state.auth);

    const { data: NameMap } = useNameMapQuery({ token: IdToken! }, { skip: !IdToken });
    const { data: OrgMap } = useOrgMapQuery({ token: IdToken! }, { skip: !IdToken });

    const mlOpts = useMemo(
        () => keymap(NameMap?.companies),
        [NameMap?.companies, OrgMap?.companyResellerMap, OrgMap?.companyLocationMap]
    );
    const rsOpts = useMemo(
        () => keymap(NameMap?.resellers),
        [NameMap?.resellers, OrgMap?.companyResellerMap, OrgMap?.resellerLocationMap]
    );
    const locOpts = useMemo(
        () => keymap(NameMap?.locations),
        [NameMap?.locations, OrgMap?.resellerLocationMap, OrgMap?.companyLocationMap]
    );

    const resetChildren =
        (children: (keyof typeof defaults | false)[], idKey: string, nameKey: string) =>
        (_: SyntheticEvent<Element, Event>, value: any) => {
            const last = Array.isArray(value) ? value[value.length - 1] : undefined;
            const { id, name } = last ?? {};

            const rsDefault = options.resellers && children.includes('reseller') ? defaults.reseller : {};
            const locDefault = options.locations && children.includes('location') ? defaults.location : {};

            setDraftState({
                ...draftState,
                [idKey]: id !== undefined ? parseInt(id) : null,
                [nameKey]: name,
                ...rsDefault,
                ...locDefault,
            });
        };

    return (
        <>
            {options.companies && (
                <FormSingleSelect
                    disable={disabled || disabledOptions?.companies}
                    size={size}
                    draft={draft}
                    label='Company'
                    includeAll={includeAll}
                    idKey={'CompanyID'}
                    nameKey={'CompanyName'}
                    options={mlOpts}
                    onChange={resetChildren(['reseller', 'location'], 'CompanyID', 'CompanyName')}
                />
            )}

            {options.resellers && (
                <FormSingleSelect
                    disable={disabled || disabledOptions?.resellers}
                    size={size}
                    includeAll={includeAll}
                    label='Reseller'
                    draft={draft}
                    idKey={'ResellerID'}
                    nameKey={'ResellerName'}
                    options={
                        options.companies
                            ? rsOpts.filter(({ id }) => {
                                  return OrgMap?.companyResellerMap[draftState?.CompanyID]?.includes(+id);
                              })
                            : rsOpts
                    }
                    onChange={resetChildren(['location'], 'ResellerID', 'ResellerName')}
                />
            )}

            {options.locations && (
                <FormSingleSelect
                    disable={disabled || disabledOptions?.locations}
                    size={size}
                    includeAll={includeAll}
                    draft={draft}
                    label='Location'
                    idKey={'LocationID'}
                    nameKey={'LocationName'}
                    options={locOpts.filter(({ id }) => {
                        return options.resellers && draftState?.ResellerID
                            ? OrgMap?.resellerLocationMap[draftState?.ResellerID]?.includes(+id)
                            : OrgMap?.companyLocationMap[draftState?.CompanyID]?.includes(+id);
                    })}
                    onChange={(e, value) => {
                        // Always update draft normally
                        resetChildren([], 'LocationID', 'LocationName')(e, value);
                        // Then run custom handler if provided
                        onLocationChange?.(e, value);
                    }}
                />
            )}
        </>
    );
};
